import { useAuth } from "@/auth/AuthContext";
import { useQuestionnaireFlow } from "@/hooks/useQuestionnaireFlow";
import { usePostHogQuestionnaireTracking } from "@/hooks/usePostHogQuestionnaireTracking";
import { QuestionnaireFlowQuestion, QuestionnaireFlowQuestionOption } from "@/types/question.types";
import { Center, Text } from "@mantine/core";
import { UseFormReturnType } from "@mantine/form";
import { useTimeout } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { PostIdentityTestInfoBreak } from "../PersonalityTest/InfoBreak/PostIdentityTestInfoBreak";
import { PostPersonalityTestInfoBreak } from "../PersonalityTest/InfoBreak/PostPersonalityTestInfoBreak";
import { QuestionAndAnswerResolver } from "../PersonalityTest/QuestionAndAnswerResolver";
import { QuestionnaireUILayout } from "./QuestionnaireUILayout";

export enum InfoBreakSlugs {
  POST_PERSONALITY_TEST_INFO_BREAK = "post_personality_test_info_break",
  POST_IDENTITY_TEST_INFO_BREAK = "post_identity_test_info_break",
}
/**
 * Renders a multi-step questionnaire, intelligently populating a form without being tied to its structure.
 *
 * This component is designed to be highly reusable. It decouples the questionnaire's presentation
 * from the form's data structure by using a mapping function provided by the consumer.
 *
 * @param {QuestionnaireFlowQuestion[]} props.questions - An array of question objects to be rendered.
 * @param {function(QuestionnaireFlowQuestion): {fieldPath: string, validationPath: string}} props.getQuestionPaths -
 *   A function that takes a question and returns the corresponding `fieldPath` and `validationPath` for the form.
 *   This is the core of its flexibility, allowing it to populate different form sections (e.g., `personal_info`, `answers`)
 *   based on the question's properties (like `category`).
 * @param {UseFormReturnType<TFormValues>} props.form - The Mantine form instance to be populated.
 * @param {function(TFormValues): void} props.onSubmit - The callback function to execute upon successful form submission.
 */
export interface QuestionnaireFlowProps<TFormValues> {
  flowType: string;
  questions: QuestionnaireFlowQuestion[];
  getQuestionPaths: (question: QuestionnaireFlowQuestion) => {
    fieldPath: string;
    validationPath: string;
  };
  form: UseFormReturnType<TFormValues>;
  onSubmit: (values: TFormValues) => void;
  isSubmitting: boolean;
  isSubmitError: boolean;
  onUnauthenticatedSubmit?: (formValues: TFormValues) => void;
}

export function QuestionnaireFlow<TFormValues>({
  flowType,
  questions,
  getQuestionPaths,
  form,
  onSubmit,
  isSubmitting,
  isSubmitError,
  onUnauthenticatedSubmit,
}: QuestionnaireFlowProps<TFormValues>) {
  const { user } = useAuth();
  const {
    currentQuestion,
    questionAnalysis,
    isLastStep,
    progressValue,
    isFirstStep,
    currentStep,
    actions: { goToNextStep, goToPreviousStep },
  } = useQuestionnaireFlow({ questions, getQuestionPaths });
  const navigate = useNavigate();

  const { trackQuestionViewed, trackQuestionAnswered } = usePostHogQuestionnaireTracking();

  useEffect(() => {
    if (currentQuestion) {
      trackQuestionViewed(flowType, currentQuestion, currentStep, questions.length);
    }
  }, [currentQuestion?.slug]);

  const { start: goToNextStepAfterDelay, clear: clearGoToNextStepDelay } = useTimeout(
    goToNextStep,
    300
  );

  const handleGoToPreviousStep = () => {
    if (isFirstStep) {
      return navigate(-1);
    }
    clearGoToNextStepDelay();
    goToPreviousStep();
  };

  const handleFormSubmit = () => {
    if (!user && onUnauthenticatedSubmit) {
      onUnauthenticatedSubmit(form.values);
    } else {
      onSubmit(form.values);
    }
  };

  if (!currentQuestion || !questionAnalysis) {
    const message = questions.length === 0 ? "No questions available." : "Error loading question.";
    return (
      <Center h="100%">
        <Text>{message}</Text>
      </Center>
    );
  }

  const handleSelectMultiple = (option: QuestionnaireFlowQuestionOption) => {
    const path = currentQuestion.fieldPath;

    if (currentQuestion.category !== "custom") {
      console.warn(
        `handleSelectMultiple is not supported for category '${currentQuestion.category}'`
      );
      return;
    }

    if (option.id === undefined) {
      console.error("handleSelectMultiple called for an option with no id", option);
      return;
    }

    const valueToStore = option.id;
    const currentSelection = (form.getInputProps(path).value as number[] | undefined) ?? [];
    const newSelection = currentSelection.includes(valueToStore)
      ? currentSelection.filter((o) => o !== valueToStore)
      : [...currentSelection, valueToStore];
    form.setFieldValue(path as any, newSelection as any);
    trackQuestionAnswered(flowType, currentQuestion, currentStep);
  };

  const handleSelectSingle = (option: QuestionnaireFlowQuestionOption) => {
    const path = currentQuestion.fieldPath;

    let valueToStore: string | number[] | undefined;

    if (currentQuestion.category === "custom") {
      if (option.id === undefined) {
        console.error("handleSelectSingle called for a custom question with no option id", option);
        return;
      }
      valueToStore = [option.id];
    } else {
      valueToStore = option.value;
    }

    form.setFieldValue(path as any, valueToStore as any);

    // Clear custom input if switching away from an option with custom input
    if (option.customInputType !== "text_input" && currentQuestion.slug === "nationality") {
      form.setFieldValue("personal_info.nationalityOtherText" as any, "" as any);
    }

    if (!form.isValid(path)) {
      notifications.show({
        title: "에러가 발생했습니다.",
        message: "다시 시도해주세요.",
        color: "red",
      });
    }

    trackQuestionAnswered(flowType, currentQuestion, currentStep);

    // Don't auto-advance if this option requires custom input
    if (option.customInputType !== "text_input") {
      goToNextStepAfterDelay();
    }
  };

  const handleSelectRange = (scaleValue: number) => {
    const path = currentQuestion.fieldPath;
    form.setFieldValue(path, scaleValue as any);
    if (!form.isValid(path)) {
      notifications.show({
        title: "에러가 발생했습니다.",
        message: "다시 시도해주세요.",
        color: "red",
      });
    } else {
      trackQuestionAnswered(flowType, currentQuestion, currentStep);
      goToNextStepAfterDelay();
    }
  };

  // Custom input handling for nationality "기타" option
  const customInputPath = "personal_info.nationalityOtherText" as const;
  const customInputValue =
    currentQuestion.slug === "nationality"
      ? (form.getInputProps(customInputPath as any).value as string)
      : undefined;
  const handleCustomInputChange = (value: string) => {
    form.setFieldValue(customInputPath as any, value as any);
  };

  // Check if "기타" is selected and requires custom text
  const isOtherOptionSelected =
    currentQuestion.slug === "nationality" &&
    form.getInputProps(currentQuestion.fieldPath as any).value === "기타";
  const isCustomTextRequired =
    isOtherOptionSelected && (!customInputValue || customInputValue.trim().length === 0);

  const isNextButtonActuallyDisabled =
    !form.isValid(currentQuestion.validationPath) || isCustomTextRequired;

  const questionArea = (
    <QuestionAndAnswerResolver
      currentQuestion={currentQuestion}
      form={form}
      handleSelectMultiple={handleSelectMultiple}
      handleSelectSingle={handleSelectSingle}
      handleSelectRange={handleSelectRange}
      fieldPath={currentQuestion.fieldPath}
      customInputValue={customInputValue}
      onCustomInputChange={handleCustomInputChange}
    />
  );

  const shouldShowNextButton =
    !isLastStep &&
    (questionAnalysis.isSelectFromListQuestion ||
      questionAnalysis.isSelectMultipleQuestion ||
      questionAnalysis.isTextQuestion ||
      questionAnalysis.isDateQuestion);

  // we have some info breaks between questions that have no questions or answers.
  // so its important we render them outside of the QuestionnaireUILayout
  if (currentQuestion.slug === InfoBreakSlugs.POST_PERSONALITY_TEST_INFO_BREAK) {
    return <PostPersonalityTestInfoBreak onNextButtonClick={goToNextStep} />;
  }

  if (currentQuestion.slug === InfoBreakSlugs.POST_IDENTITY_TEST_INFO_BREAK) {
    return (
      <PostIdentityTestInfoBreak
        onNextButtonClick={isLastStep ? handleFormSubmit : goToNextStep}
        buttonText={isLastStep ? "제출" : "다음"}
      />
    );
  }

  return (
    <QuestionnaireUILayout
      progressValue={progressValue}
      showBackButton={!!currentQuestion && currentQuestion.slug !== "gender"} // Assuming gender is the first question
      onBackButtonClick={handleGoToPreviousStep}
      title={currentQuestion.title}
      subtitle={currentQuestion.subtitle ?? undefined}
      questionAreaContent={questionArea}
      showNextButton={shouldShowNextButton}
      onNextButtonClick={goToNextStep}
      isNextButtonDisabled={isNextButtonActuallyDisabled}
      showSubmitButton={isLastStep}
      onSubmitButtonClick={handleFormSubmit}
      isSubmitButtonDisabled={isNextButtonActuallyDisabled || isSubmitting}
      isErrorState={isSubmitError}
      errorMessage={isSubmitError ? "An error occurred. Please try again." : undefined}
      pbForQuestionTitle={Boolean(currentQuestion?.extra?.options?.length ?? 0 > 6) ? "sm" : "xl"}
      isNextButtonLoading={isSubmitting}
      isSubmitButtonLoading={isSubmitting}
    />
  );
}
